<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import OrgTreePicker from '@/components/OrgTreePicker.vue';
import PersonPicker from '@/components/PersonPicker.vue';
import type { TeamPerson } from '@/components/TeamMemberQuickAdd.vue';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage } from '@/utils/format';

/**
 * 移出团队成员前交接弹窗（batch-reassign UX 2026-10-10）。
 * 硬闸：有 user_id 的阶段任务 / 审批 / 派活 / 交付物 / 问题·8D(有 uid) —— 必须改派完才能移出。
 * 软提示：仅姓名问题 / 8D / 交付物 —— 勾选「已人工处理」即可，不自动改派、不拦 DELETE。
 *
 * 相对 handoff v1 的最小 UX 修复（不改 API）：
 * 1) 顶部选系统账号（userId）→「全部指派此人」填满硬闸接手人
 * 2) 一键「确认改派并移出」：先 POST reassign（含团队外自动入队）再 DELETE
 */
export interface HandoffOpenItem {
  kind: string;
  gate: string;
  id: number;
  title: string;
  status: string | null;
  dueAt: string | null;
  projectId: number;
  match: string;
}

const props = defineProps<{
  open: boolean;
  projectId: string | number;
  member: ModuleRecord;
  people: TeamPerson[];
  teamMembers: ModuleRecord[];
}>();

const emit = defineEmits<{
  close: [];
  removed: [];
}>();

const loading = ref(false);
const saving = ref(false);
const removing = ref(false);
const hard = ref<HandoffOpenItem[]>([]);
const soft = ref<HandoffOpenItem[]>([]);
const memberName = ref('');
const softHandled = ref<Record<string, boolean>>({});
const targetByKey = ref<Record<string, number | null>>({});
const bulkTarget = ref<number | null>(null);
const orgOpen = ref(false);
const orgPicked = ref<TeamPerson | null>(null);
const addRoleTitle = ref('项目协作');

const KIND_LABEL: Record<string, string> = {
  'plan-node': '阶段任务',
  deliverable: '交付物',
  approval: '审批待办',
  delegation: '派活',
  issue: '问题',
  '8d-step': '8D步骤',
  'deliverable-name': '交付物(仅姓名)',
};

function itemKey(item: HandoffOpenItem): string {
  return `${item.kind}:${item.id}`;
}

const softAllHandled = computed(() =>
  soft.value.every((item) => softHandled.value[itemKey(item)]));

const hardReady = computed(() => hard.value.length === 0);

const hardAllAssigned = computed(() =>
  hard.value.length > 0
  && hard.value.every((item) => {
    const uid = targetByKey.value[itemKey(item)];
    return uid != null && Number.isInteger(uid) && uid > 0;
  }));

const canRemove = computed(() =>
  hardReady.value && softAllHandled.value && !loading.value && !saving.value && !removing.value);

/** 硬闸已全部选好接手人（尚未改派）时可一键改派并移出 */
const canReassignAndRemove = computed(() =>
  hardAllAssigned.value && softAllHandled.value && !loading.value && !saving.value && !removing.value);

const teamPeople = computed<TeamPerson[]>(() => {
  const fromTeam: TeamPerson[] = props.teamMembers
    .map((m) => {
      const id = Number(m.parent_id ?? m.parentId);
      if (!Number.isFinite(id) || id < 1) return null;
      return {
        id,
        displayName: String(m.title ?? m.name ?? '').trim() || `用户#${id}`,
        orgName: String(m.code ?? '').trim() || null,
        email: (m.owner_name as string | null) ?? null,
      } satisfies TeamPerson;
    })
    .filter((p): p is TeamPerson => p !== null);
  const map = new Map<number, TeamPerson>();
  for (const p of [...fromTeam, ...props.people]) map.set(p.id, p);
  if (orgPicked.value) map.set(orgPicked.value.id, orgPicked.value);
  return [...map.values()].sort((a, b) => a.displayName.localeCompare(b.displayName, 'zh'));
});

const bulkPerson = computed(() =>
  bulkTarget.value == null
    ? null
    : teamPeople.value.find((p) => p.id === bulkTarget.value) ?? null);

async function load(): Promise<void> {
  loading.value = true;
  try {
    const data = await eqsApi.teamMemberOpenItems(props.projectId, props.member.id);
    memberName.value = data.member.name;
    hard.value = data.hard as HandoffOpenItem[];
    soft.value = data.soft as HandoffOpenItem[];
    softHandled.value = {};
    targetByKey.value = {};
    for (const item of hard.value) targetByKey.value[itemKey(item)] = null;
    for (const item of soft.value) softHandled.value[itemKey(item)] = false;
  } catch (caught) {
    notify('error', '读取开放事项失败', errorMessage(caught));
    emit('close');
  } finally {
    loading.value = false;
  }
}

watch(() => props.open, (open) => {
  if (!open) return;
  bulkTarget.value = null;
  orgPicked.value = null;
  orgOpen.value = false;
  void load();
}, { immediate: true });

function setBulkTarget(userId: number | null): void {
  bulkTarget.value = userId;
}

function applyBulk(): void {
  if (bulkTarget.value == null) {
    notify('error', '请先选择接手人账号', '从建议列表选唯一匹配，或按部门找人');
    return;
  }
  const next = { ...targetByKey.value };
  for (const item of hard.value) next[itemKey(item)] = bulkTarget.value;
  targetByKey.value = next;
  const who = bulkPerson.value?.displayName ?? `用户#${bulkTarget.value}`;
  notify('success', '已全部指派', `${who} → ${hard.value.length} 项硬闸（尚未提交，请确认改派并移出）`);
}

function onBulkPicker(userId: number | null): void {
  setBulkTarget(userId);
  if (userId != null) applyBulk();
}

function onRowPicker(item: HandoffOpenItem, userId: number | null): void {
  targetByKey.value = { ...targetByKey.value, [itemKey(item)]: userId };
}

function onOrgPicked(
  userId: number | null,
  member: { id: number; displayName: string; email: string | null } | null,
): void {
  if (!userId) return;
  if (member) {
    orgPicked.value = { id: member.id, displayName: member.displayName, orgName: null, email: member.email };
  }
  setBulkTarget(userId);
  applyBulk();
  orgOpen.value = false;
}

function buildReassignItems(): Array<{ kind: string; id: number; toUserId: number }> | null {
  const items = hard.value.map((item) => {
    const toUserId = targetByKey.value[itemKey(item)];
    if (toUserId == null) return null;
    return { kind: item.kind, id: item.id, toUserId };
  });
  if (items.some((item) => item == null)) {
    notify('error', '请为每条硬闸事项选择接手人账号', '');
    return null;
  }
  return items as Array<{ kind: string; id: number; toUserId: number }>;
}

async function reassignAll(): Promise<boolean> {
  const items = buildReassignItems();
  if (!items) return false;
  saving.value = true;
  try {
    const result = await eqsApi.teamMemberReassign(props.projectId, props.member.id, {
      items,
      addRoleTitle: addRoleTitle.value.trim() || undefined,
    });
    notify(
      'success',
      '已改派',
      `改派 ${result.reassigned} 项`
        + (result.addedMember ? '；接手人不在团队，已自动加入' : '')
        + (result.remainingHard ? `；仍剩 ${result.remainingHard} 项硬闸` : ''),
    );
    await load();
    return result.remainingHard === 0;
  } catch (caught) {
    notify('error', '改派失败', errorMessage(caught));
    return false;
  } finally {
    saving.value = false;
  }
}

async function removeNow(): Promise<void> {
  if (!canRemove.value) return;
  removing.value = true;
  try {
    await eqsApi.removeProjectMember(props.member.id);
    notify('success', '已移出项目', memberName.value || String(props.member.title ?? ''));
    emit('removed');
    emit('close');
  } catch (caught) {
    notify('error', '移出失败', errorMessage(caught));
    await load();
  } finally {
    removing.value = false;
  }
}

/** 一键：先改派硬闸（现有 reassign + 自动入队），成功且无剩余硬闸后再移出 */
async function reassignAndRemove(): Promise<void> {
  if (!canReassignAndRemove.value && !canRemove.value) return;
  if (hardReady.value) {
    await removeNow();
    return;
  }
  const ok = await reassignAll();
  if (!ok) return;
  // load() 后 hard 应为空；软提示需仍满足
  if (!hardReady.value) {
    notify('error', '改派后仍有硬闸未清', '请检查后重试');
    return;
  }
  if (!softAllHandled.value) {
    notify('error', '请勾选软提示「已人工处理」', '');
    return;
  }
  await removeNow();
}

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.open && !saving.value && !removing.value) emit('close');
}
onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <div v-if="open" class="ho-mask" @click.self="!saving && !removing && emit('close')">
    <section class="ho-dialog" role="dialog" aria-modal="true" aria-labelledby="ho-title">
      <header>
        <div>
          <span class="ho-kicker">TEAM / HANDOFF</span>
          <h3 id="ho-title">移出前交接 · {{ memberName || member.title }}</h3>
          <p>
            有账号的开放事项必须改派后才能移出；问题 / 8D / 仅姓名交付物仅作提示，勾选「已人工处理」即可。
            顶部先选<strong>系统账号</strong>（非手打姓名），点「全部指派此人」填满硬闸，再一键确认改派并移出（团队外自动入队）。
          </p>
        </div>
        <button class="inline-button" type="button" :disabled="saving || removing" @click="emit('close')">关闭</button>
      </header>

      <div v-if="loading" class="ho-body">正在读取开放事项…</div>
      <div v-else class="ho-body">
        <datalist id="ho-bulk-people">
          <option v-for="p in teamPeople" :key="'b'+p.id" :value="p.displayName" />
        </datalist>
        <datalist id="ho-row-people">
          <option v-for="p in teamPeople" :key="'r'+p.id" :value="p.displayName" />
        </datalist>
        <section class="ho-block">
          <h4>硬闸事项（{{ hard.length }}）— 必须改派</h4>
          <p v-if="!hard.length" class="ho-ok">无硬闸开放项，可以移出。</p>
          <template v-else>
            <div class="ho-bulk">
              <label>
                接手人账号（顶部统一指定）
                <PersonPicker
                  :people="teamPeople"
                  :model-value="bulkTarget"
                  label="批量接手人账号"
                  list-id="ho-bulk-people"
                  placeholder="从建议选账号，勿只手打姓名"
                  @update:model-value="onBulkPicker"
                />
              </label>
              <button
                class="button button-secondary"
                type="button"
                :disabled="bulkTarget == null || saving || removing"
                @click="applyBulk"
              >
                全部指派此人
              </button>
              <button class="button button-secondary" type="button" @click="orgOpen = !orgOpen">按部门找人</button>
              <label class="ho-role">
                自动入队角色
                <input v-model="addRoleTitle" type="text" maxlength="80">
              </label>
            </div>
            <p v-if="bulkPerson" class="ho-account">
              已选账号：<strong>{{ bulkPerson.displayName }}</strong>
              <span class="ho-muted">#{{ bulkPerson.id }}</span>
              <span v-if="bulkPerson.email" class="ho-muted">{{ bulkPerson.email }}</span>
            </p>
            <OrgTreePicker v-if="orgOpen" :model-value="bulkTarget" @update:model-value="onOrgPicked" />
            <table class="ho-table">
              <thead>
                <tr><th>类型</th><th>事项</th><th>状态</th><th>接手人账号</th></tr>
              </thead>
              <tbody>
                <tr v-for="item in hard" :key="itemKey(item)">
                  <td>{{ KIND_LABEL[item.kind] || item.kind }}</td>
                  <td>{{ item.title }}</td>
                  <td>{{ item.status || '—' }}</td>
                  <td>
                    <PersonPicker
                      :people="teamPeople"
                      :model-value="targetByKey[itemKey(item)] ?? null"
                      :label="`接手 ${item.title}`"
                      list-id="ho-row-people"
                      placeholder="选账号"
                      @update:model-value="(v) => onRowPicker(item, v)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <button class="button button-secondary" type="button" :disabled="saving || !hardAllAssigned" @click="reassignAll">
              {{ saving ? '正在改派…' : '仅改派（不移出）' }}
            </button>
          </template>
        </section>

        <section class="ho-block">
          <h4>软提示（{{ soft.length }}）— 不拦移出 · 请人工确认</h4>
          <p v-if="!soft.length" class="ho-muted">无姓名匹配的问题 / 8D / 仅姓名交付物。</p>
          <ul v-else class="ho-soft">
            <li v-for="item in soft" :key="itemKey(item)">
              <label>
                <input v-model="softHandled[itemKey(item)]" type="checkbox">
                <span class="ho-approx">≈</span>
                <strong>{{ KIND_LABEL[item.kind] || item.kind }}</strong>
                {{ item.title }}
                <em>{{ item.status || '' }}</em>
                <span class="ho-muted">已人工处理</span>
              </label>
            </li>
          </ul>
        </section>
      </div>

      <footer>
        <button
          v-if="hard.length"
          class="button button-primary"
          type="button"
          :disabled="!canReassignAndRemove"
          @click="reassignAndRemove"
        >
          {{ saving || removing ? '处理中…' : '确认改派并移出' }}
        </button>
        <button
          v-else
          class="button button-primary"
          type="button"
          :disabled="!canRemove"
          @click="removeNow"
        >
          {{ removing ? '正在移出…' : '确认移出项目' }}
        </button>
        <button class="button" type="button" :disabled="saving || removing" @click="emit('close')">取消</button>
        <span v-if="hard.length && !hardAllAssigned" class="ho-warn">还有硬闸未选接手人账号</span>
        <span v-else-if="!softAllHandled" class="ho-warn">请勾选软提示「已人工处理」</span>
        <span v-else-if="hard.length" class="ho-ok">可一键改派并移出</span>
      </footer>
    </section>
  </div>
</template>

<style scoped>
.ho-mask { position: fixed; inset: 0; z-index: 70; display: flex; align-items: flex-start; justify-content: center; padding: 4vh 16px; background: rgba(15, 23, 36, .45); overflow-y: auto; }
.ho-dialog { width: min(880px, 100%); border: 1px solid var(--line); border-radius: var(--radius); background: var(--white); box-shadow: 0 18px 48px rgba(15, 23, 36, .22); }
.ho-dialog > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--line); }
.ho-kicker { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.ho-dialog header h3 { margin: 3px 0 0; font-size: 18px; }
.ho-dialog header p { margin: 4px 0 0; max-width: 72ch; color: var(--muted); font-size: 13px; line-height: 1.6; }
.ho-body { display: grid; gap: 18px; padding: 16px 20px; }
.ho-block h4 { margin: 0 0 8px; font-size: 14px; }
.ho-ok { color: var(--green); font-size: 13px; }
.ho-muted { color: var(--muted); font-size: 12px; }
.ho-warn { color: var(--warning); font-size: 12px; font-weight: 700; }
.ho-account { margin: 0 0 8px; font-size: 13px; }
.ho-bulk { display: flex; flex-wrap: wrap; gap: 10px; align-items: end; margin-bottom: 10px; }
.ho-bulk label, .ho-role { display: grid; gap: 4px; font-size: 12px; font-weight: 700; }
.ho-bulk input, .ho-role input { min-height: 34px; padding: 4px 8px; border: 1px solid var(--line-strong); font: inherit; }
.ho-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 10px; }
.ho-table th, .ho-table td { border-bottom: 1px solid var(--line); padding: 8px 6px; text-align: left; vertical-align: middle; }
.ho-soft { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.ho-soft label { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 13px; }
.ho-approx { color: var(--warning); font-weight: 700; }
.ho-dialog footer { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding: 12px 20px 16px; border-top: 1px solid var(--line); }
</style>
